import { useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { type FundingPolicyType } from '@shared';
import {
  useFormConfig,
  useFormDefinition,
  useFormMetadata,
  useFormIsDirty,
  getFormBuilderActions,
} from '@/features/quote-and-apply-builder';
import { useSaveForm } from '@/features/quote-and-apply-builder/hooks/use-save-form';
import { toast } from 'sonner';

export function useFormEditorPage() {
  const { carrierId, formId } = useParams<{ carrierId: string; formId: string }>();
  const navigate = useNavigate();
  const { data: formConfig, isLoading, error } = useFormConfig(formId!);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const handleBack = useCallback(() => {
    navigate(`/wizard/form-builder/${carrierId}`);
  }, [navigate, carrierId]);

  return {
    carrierId: carrierId!,
    formConfig,
    isLoading,
    error,
    handleBack,
  };
}

interface UseFormEditorLoadedParams {
  formConfig: PolicyFormConfig;
  fundingPolicyTypeId?: FundingPolicyType[];
  stateCode?: string;
}

export function useFormEditorLoaded({
  formConfig,
  fundingPolicyTypeId,
  stateCode,
}: UseFormEditorLoadedParams) {
  const { initialize, reset, resetDirty } = getFormBuilderActions();

  useEffect(() => {
    initialize(formConfig);
    return () => reset();
  }, [formConfig, initialize, reset]);

  const formDefinition = useFormDefinition();
  const formMetadata = useFormMetadata();
  const isDirty = useFormIsDirty();

  const { mutate: saveForm, isPending: isSaving } = useSaveForm();

  const resolvedPolicyTypeIds =
    fundingPolicyTypeId ??
    (formConfig.fundingPolicyTypeId
      ? [formConfig.fundingPolicyTypeId as FundingPolicyType]
      : undefined);
  const resolvedStateCode = stateCode ?? formConfig.stateCode;

  const canSave = isDirty && !!resolvedPolicyTypeIds?.length;

  const handleSave = useCallback(() => {
    if (!resolvedPolicyTypeIds?.length) return;

    saveForm(
      {
        formDefinition,
        formMetadata,
        name: formConfig.name,
        fundingPolicyTypeId: resolvedPolicyTypeIds,
        ...(resolvedStateCode && { stateCode: resolvedStateCode }),
        dryRun: false,
      },
      {
        onSuccess: () => {
          resetDirty();
          toast.success('Form saved successfully');
        },
        onError: saveError => {
          toast.error(saveError instanceof Error ? saveError.message : 'Failed to save form');
        },
      },
    );
  }, [
    formDefinition,
    formMetadata,
    formConfig.name,
    resolvedPolicyTypeIds,
    resolvedStateCode,
    saveForm,
    resetDirty,
  ]);

  return {
    canSave,
    isSaving,
    handleSave,
  };
}
